import _extends from "@babel/runtime/helpers/extends";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _unionBy from "lodash/unionBy";
import _values from "lodash/values";
var _excluded = ["registerMarketplaceEntities"];
import React, { useCallback, useEffect, useState } from 'react';
import hoistNonReactStatics from 'hoist-non-react-statics';
import { getId, isActive, getProductType } from '@nerdwallet/marketplace/selectors/common';
import { PRODUCT_TYPES } from '@nerdwallet/marketplace/constants';
import getDisplayName from '../../lib/get-display-name';
var COMPLIANCE_OK_FIELDS = ['name', 'product.name', 'institution.name', 'product.institution.name', 'details.seo_name', 'derived_details.reward_cents_per_unit_string'];

var getEntitiesFromProps = function getEntitiesFromProps(props) {
  var _props$data$value, _props$data;

  return _values((_props$data$value = props === null || props === void 0 ? void 0 : (_props$data = props.data) === null || _props$data === void 0 ? void 0 : _props$data.value) !== null && _props$data$value !== void 0 ? _props$data$value : {});
};

export var isCreditCardProduct = function isCreditCardProduct(entity) {
  return getProductType(entity) === PRODUCT_TYPES.CREDIT_CARDS;
};
export var getInactiveEntitiesFromProps = function getInactiveEntitiesFromProps(props) {
  var entities = getEntitiesFromProps(props);
  var inactiveEntities = [];
  if (!entities.length) return []; // Ignore MarketplaceField components when the field is not a compliance-flagged field.
  // We don't need to show the out-of date messaging if this field isn't a compliance concern.

  if (COMPLIANCE_OK_FIELDS.indexOf(props.field) > -1) return [];
  entities.forEach(function (entity) {
    if (!entity || !isCreditCardProduct(entity) || isActive(entity)) {
      return;
    }

    inactiveEntities.push(entity);
  });
  return inactiveEntities;
};
var MarketplaceContext = /*#__PURE__*/React.createContext({});
export default MarketplaceContext; // Provider consumes values from parent
// and falls back to providing its own if undefined

var MarketplaceContextProvider = function MarketplaceContextProvider(_ref) {
  var children = _ref.children;

  var _useState = useState([]),
      _useState2 = _slicedToArray(_useState, 2),
      entities = _useState2[0],
      setEntities = _useState2[1];

  var _useState3 = useState([]),
      _useState4 = _slicedToArray(_useState3, 2),
      inactiveEntities = _useState4[0],
      setInactiveEntities = _useState4[1];

  var registerMarketplaceEntities = useCallback(function (props) {
    var _ref2 = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {},
        _ref2$registerInactiv = _ref2.registerInactive,
        registerInactive = _ref2$registerInactiv === void 0 ? true : _ref2$registerInactiv;

    var newEntities = _unionBy(entities, getEntitiesFromProps(props), function (entity) {
      return getId(entity);
    });

    if (newEntities.length !== entities.length) setEntities(newEntities);

    if (registerInactive) {
      var newInactiveEntities = _unionBy(inactiveEntities, registerInactive ? getInactiveEntitiesFromProps(props) : [], function (entity) {
        return getId(entity);
      });

      if (newInactiveEntities.length !== inactiveEntities.length) setInactiveEntities(newInactiveEntities);
    }
  }, [entities, inactiveEntities]);
  return /*#__PURE__*/React.createElement(MarketplaceContext.Consumer, null, function (context) {
    return /*#__PURE__*/React.createElement(MarketplaceContext.Provider, {
      value: {
        registerMarketplaceEntities: context.registerMarketplaceEntities || registerMarketplaceEntities,
        entities: context.entities || entities,
        inactiveEntities: context.inactiveEntities || inactiveEntities
      }
    }, children);
  });
};

export { MarketplaceContextProvider };

/**
 * HOC that takes a Component and options object and registers marketplace entities
 * into a MarketplaceContext after entities are resolved in props.data
 *
 * @export
 * @param {*} Component
 * @param {*} RegisterMarketplaceEntityOptions options
 * @returns wrapped component
 */
export function withMarketplaceContext(Component) {
  var options = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};

  var WithMarketplaceContextImpl = function WithMarketplaceContextImpl(_ref3) {
    var registerMarketplaceEntities = _ref3.registerMarketplaceEntities,
        props = _objectWithoutProperties(_ref3, _excluded);

    useEffect(function () {
      var _props$data2, _props$data3;

      if (props !== null && props !== void 0 && (_props$data2 = props.data) !== null && _props$data2 !== void 0 && _props$data2.loading) return;
      if (!(props !== null && props !== void 0 && (_props$data3 = props.data) !== null && _props$data3 !== void 0 && _props$data3.value)) return;
      registerMarketplaceEntities(props, options);
    }, [props, registerMarketplaceEntities]);
    return /*#__PURE__*/React.createElement(Component, props);
  };

  var WithMarketplaceContext = function WithMarketplaceContext(props) {
    return /*#__PURE__*/React.createElement(MarketplaceContext.Consumer, null, function (_ref4) {
      var registerMarketplaceEntities = _ref4.registerMarketplaceEntities;
      return /*#__PURE__*/React.createElement(WithMarketplaceContextImpl, _extends({}, props, {
        registerMarketplaceEntities: registerMarketplaceEntities
      }));
    });
  };

  hoistNonReactStatics(WithMarketplaceContext, Component);
  WithMarketplaceContext.displayName = "WithMarketplaceContext(".concat(getDisplayName(Component), ")");
  return WithMarketplaceContext;
}